import { useState, useMemo, useEffect } from "react";
import { HiCog6Tooth, HiXMark, HiPlus, HiPencil, HiTrash, HiEye, HiEyeSlash, HiCheck, HiBars3 } from "react-icons/hi2";
import {
  useExternalLinksGroups,
  useCreateGroup,
  useUpdateGroup,
  useDeleteGroup,
  useCreateLink,
  useUpdateLink,
  useDeleteLink,
} from "../hooks/useExternalLinks";
import { useUserPreferences, useSetUserPreferences } from "../hooks/useUserPreferences";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "./ui/tooltip";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  horizontalListSortingStrategy,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface ExternalLinksSectionProps {
  userEmail: string;
}

// Normalize URL for comparison (removes protocol, www, trailing slash, lowercases)
function normalizeUrl(url: string): string {
  return url
    .toLowerCase()
    .replace(/^https?:\/\//, '') // Remove protocol
    .replace(/^www\./, '')        // Remove www.
    .replace(/\/+$/, '');         // Remove trailing slashes
}

type Group = {
  _id: string;
  name: string;
  sortOrder: number;
  links: Array<{
    _id: string;
    name: string;
    url: string;
    description?: string;
    tags: string[];
    sortOrder: number;
  }>;
};

export default function ExternalLinksSection({ userEmail }: ExternalLinksSectionProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [showManageModal, setShowManageModal] = useState(false);
  const [hiddenLinkIds, setHiddenLinkIds] = useState<Set<string>>(new Set());
  const [isReordering, setIsReordering] = useState(false);
  const [orderedLinks, setOrderedLinks] = useState<Map<string, string[]>>(new Map());
  const [orderedGroupIds, setOrderedGroupIds] = useState<string[]>([]);

  const { data: groupsWithLinks, isLoading } = useExternalLinksGroups();
  const { data: userPrefs } = useUserPreferences(userEmail);
  const setPreferencesMutation = useSetUserPreferences();

  // Cast userPrefs to expected shape
  const prefs = userPrefs as { hiddenLinks?: string[]; linkOrder?: Record<string, string[]>; groupOrder?: string[] } | undefined;

  // Load hidden links, link order, and group order from user preferences
  useEffect(() => {
    const savedHidden = prefs?.hiddenLinks;
    if (savedHidden && savedHidden.length > 0) {
      setHiddenLinkIds(new Set(savedHidden));
    }
    const savedOrder = prefs?.linkOrder;
    if (savedOrder) {
      setOrderedLinks(new Map(Object.entries(savedOrder)));
    }
    const savedGroupOrder = prefs?.groupOrder;
    if (savedGroupOrder && savedGroupOrder.length > 0) {
      setOrderedGroupIds(savedGroupOrder);
    }
  }, [prefs]);

  const toggleLinkVisibility = async (linkId: string) => {
    const newHidden = new Set(hiddenLinkIds);
    if (newHidden.has(linkId)) {
      newHidden.delete(linkId);
    } else {
      newHidden.add(linkId);
    }
    setHiddenLinkIds(newHidden);

    // Save to preferences
    await setPreferencesMutation.mutateAsync({
      userId: userEmail,
      preferences: {
        ...prefs,
        hiddenLinks: Array.from(newHidden),
      },
    });
  };

  const toggleGroupVisibility = async (group: Group) => {
    const newHidden = new Set(hiddenLinkIds);
    const allHidden = group.links.every((link) => newHidden.has(link._id));

    if (allHidden) {
      // Show all
      group.links.forEach((link) => newHidden.delete(link._id));
    } else {
      // Hide all
      group.links.forEach((link) => newHidden.add(link._id));
    }
    setHiddenLinkIds(newHidden);

    await setPreferencesMutation.mutateAsync({
      userId: userEmail,
      preferences: {
        ...prefs,
        hiddenLinks: Array.from(newHidden),
      },
    });
  };

  // Sort links within a group based on user's custom order, or alphabetically by default
  const sortLinksInGroup = (links: Group["links"], groupId: string) => {
    const customOrder = orderedLinks.get(groupId);
    if (!customOrder || customOrder.length === 0) {
      // Default: sort alphabetically by name
      return [...links].sort((a, b) => a.name.localeCompare(b.name));
    }
    const orderMap = new Map(customOrder.map((id, index) => [id, index]));
    return [...links].sort((a, b) => {
      const aOrder = orderMap.get(a._id) ?? Infinity;
      const bOrder = orderMap.get(b._id) ?? Infinity;
      if (aOrder === bOrder) return a.name.localeCompare(b.name);
      return aOrder - bOrder;
    });
  };

  // Sort groups based on user's custom order
  const sortGroups = (groups: Group[]) => {
    if (orderedGroupIds.length === 0) {
      return groups;
    }
    const orderMap = new Map(orderedGroupIds.map((id, index) => [id, index]));
    return [...groups].sort((a, b) => {
      const aOrder = orderMap.get(a._id) ?? Infinity;
      const bOrder = orderMap.get(b._id) ?? Infinity;
      if (aOrder === bOrder) return a.sortOrder - b.sortOrder;
      return aOrder - bOrder;
    });
  };

  // Filter links based on search (name or tags) and visibility
  const filteredGroups = useMemo(() => {
    if (!groupsWithLinks) return [];

    const query = searchQuery.toLowerCase().trim();

    const mapped = groupsWithLinks
      .map((group) => {
        // Filter out hidden links first
        let visibleLinks = group.links.filter((link) => !hiddenLinkIds.has(link._id));

        // Apply custom order
        visibleLinks = sortLinksInGroup(visibleLinks, group._id);

        // If no search query, return group with visible links
        if (!query) {
          return { ...group, links: visibleLinks };
        }

        // If group name matches, show all visible links in that group
        if (group.name.toLowerCase().includes(query)) {
          return { ...group, links: visibleLinks };
        }

        // Otherwise, filter links by name/tags
        return {
          ...group,
          links: visibleLinks.filter(
            (link) =>
              link.name.toLowerCase().includes(query) ||
              link.tags.some((tag) => tag.toLowerCase().includes(query))
          ),
        };
      })
      .filter((group) => group.links.length > 0);

    // Apply custom group order
    return sortGroups(mapped);
  }, [groupsWithLinks, searchQuery, hiddenLinkIds, orderedLinks, orderedGroupIds]);

  const visibleGroups = filteredGroups;

  // Handle drag end for reordering links within a group
  const handleDragEnd = async (groupId: string, event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const group = visibleGroups.find((g) => g._id === groupId);
    if (!group) return;

    const oldIndex = group.links.findIndex((link) => link._id === active.id);
    const newIndex = group.links.findIndex((link) => link._id === over.id);

    const newOrder = arrayMove(group.links, oldIndex, newIndex).map((link) => link._id);

    const newOrderedLinks = new Map(orderedLinks);
    newOrderedLinks.set(groupId, newOrder);
    setOrderedLinks(newOrderedLinks);

    // Save to preferences
    await setPreferencesMutation.mutateAsync({
      userId: userEmail,
      preferences: {
        ...prefs,
        linkOrder: Object.fromEntries(newOrderedLinks),
      },
    });
  };

  // Handle drag end for reordering groups
  const handleGroupDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = visibleGroups.findIndex((g) => g._id === active.id);
    const newIndex = visibleGroups.findIndex((g) => g._id === over.id);

    const newOrder = arrayMove(visibleGroups, oldIndex, newIndex).map((g) => g._id);
    setOrderedGroupIds(newOrder);

    // Save to preferences
    await setPreferencesMutation.mutateAsync({
      userId: userEmail,
      preferences: {
        ...prefs,
        groupOrder: newOrder,
      },
    });
  };

  const handleResetOrder = async () => {
    setOrderedLinks(new Map());
    setOrderedGroupIds([]);
    const { linkOrder: _, groupOrder: __, ...restPrefs } = prefs || {};
    await setPreferencesMutation.mutateAsync({
      userId: userEmail,
      preferences: restPrefs,
    });
  };

  // Helper to highlight search matches
  const highlightMatch = (text: string) => {
    if (!searchQuery.trim()) return text;
    const query = searchQuery.trim();
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, i) =>
      regex.test(part) ? (
        <mark key={i} className="bg-yellow-200 text-gray-900 rounded px-0.5">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  // Check if a link has duplicates (for main display)
  const getLinkDuplicateWarnings = (link: Group["links"][0], currentGroupId: string): string[] => {
    if (!groupsWithLinks) return [];
    const warnings: string[] = [];
    const allLinks = groupsWithLinks.flatMap((g) =>
      g.links.map((l) => ({ ...l, groupId: g._id, groupName: g.name }))
    );

    // Check for duplicate name in same group
    const duplicateName = allLinks.find(
      (l) =>
        l._id !== link._id &&
        l.groupId === currentGroupId &&
        l.name.toLowerCase() === link.name.toLowerCase()
    );
    if (duplicateName) {
      warnings.push(`Duplicate name: another "${duplicateName.name}" exists in this group`);
    }

    // Check for duplicate URL (normalized)
    const normalizedUrl = normalizeUrl(link.url);
    const duplicateUrl = allLinks.find(
      (l) =>
        l._id !== link._id &&
        normalizeUrl(l.url) === normalizedUrl
    );
    if (duplicateUrl) {
      const isSameGroup = duplicateUrl.groupId === currentGroupId;
      if (link.url !== duplicateUrl.url) {
        warnings.push(
          `Duplicate URL: "${duplicateUrl.name}"${isSameGroup ? '' : ` in ${duplicateUrl.groupName}`} has same URL (${duplicateUrl.url} ≈ ${link.url})`
        );
      } else {
        warnings.push(
          `Duplicate URL: same as "${duplicateUrl.name}"${isSameGroup ? '' : ` in ${duplicateUrl.groupName}`}`
        );
      }
    }

    return warnings;
  };

  return (
    <section className="px-6 py-12 bg-maven-gray-800">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h2 className="font-bold text-white text-5xl">Other Links</h2>
          <div className="flex items-center gap-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-3 pr-10 py-2 border border-white rounded-full text-sm bg-transparent text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-maven-teal-500 focus:border-transparent"
              />
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="w-5 h-5 absolute right-3 top-1/2 -translate-y-1/2 text-white/50"
              >
                <path
                  fillRule="evenodd"
                  d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            {isReordering && (
              <button
                onClick={handleResetOrder}
                className="text-sm text-white/50 hover:text-white px-3 py-1.5 rounded hover:bg-white/10"
              >
                Reset Order
              </button>
            )}
            <button
              onClick={() => setIsReordering(!isReordering)}
              className={`text-sm px-3 py-1.5 rounded transition-colors ${
                isReordering
                  ? "bg-maven-teal-500 text-white"
                  : "text-white/50 hover:text-white hover:bg-white/10"
              }`}
            >
              {isReordering ? "Done" : "Reorder"}
            </button>
            <button
              onClick={() => setShowManageModal(true)}
              className="text-white/50 hover:text-white p-1"
              title="Manage links"
            >
              <HiCog6Tooth className="w-6 h-6" />
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-white/20 border-t-white rounded-full animate-spin" />
          </div>
        ) : visibleGroups.length === 0 ? (
          <div className="text-center py-12 text-white/50">
            {searchQuery ? "No links match your search" : "No links yet. Click the gear icon to add some!"}
          </div>
        ) : isReordering ? (
          <SortableGroupsContainer
            groups={visibleGroups}
            onGroupDragEnd={handleGroupDragEnd}
            onLinkDragEnd={handleDragEnd}
          />
        ) : (
          <div className="space-y-10">
            {visibleGroups.map((group, groupIdx) => (
              <div key={group._id}>
                <h3 className="text-sm font-semibold text-white mb-3">
                  {highlightMatch(group.name)}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {group.links.map((link) => {
                    const warnings = getLinkDuplicateWarnings(link, group._id);
                    return (
                      <a
                        key={link._id}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 text-sm border border-white rounded-full text-white hover:bg-white/10 transition-colors inline-flex items-center gap-1.5"
                      >
                        {warnings.length > 0 && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-help" onClick={(e) => e.preventDefault()}>⚠️</span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs bg-gray-900 text-white">
                              <ul className="text-xs space-y-1">
                                {warnings.map((warning, i) => (
                                  <li key={i}>{warning}</li>
                                ))}
                              </ul>
                            </TooltipContent>
                          </Tooltip>
                        )}
                        {highlightMatch(link.name)}
                        {link.description && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span
                                className="ml-1 w-4 h-4 rounded-full bg-white/20 hover:bg-white/40 text-white text-xs font-bold flex items-center justify-center cursor-help"
                                onClick={(e) => e.preventDefault()}
                              >
                                i
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs">
                              {link.description}
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </a>
                    );
                  })}
                </div>
                {groupIdx < visibleGroups.length - 1 && (
                  <hr className="mt-6 border-white" />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {showManageModal && (
        <ManageLinksModal
          groups={groupsWithLinks || []}
          userEmail={userEmail}
          onClose={() => setShowManageModal(false)}
          hiddenLinkIds={hiddenLinkIds}
          onToggleLinkVisibility={toggleLinkVisibility}
          onToggleGroupVisibility={toggleGroupVisibility}
        />
      )}
    </section>
  );
}

// Management Modal
function ManageLinksModal({
  groups,
  userEmail,
  onClose,
  hiddenLinkIds,
  onToggleLinkVisibility,
  onToggleGroupVisibility,
}: {
  groups: Group[];
  userEmail: string;
  onClose: () => void;
  hiddenLinkIds: Set<string>;
  onToggleLinkVisibility: (linkId: string) => void;
  onToggleGroupVisibility: (group: Group) => void;
}) {
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [editingLink, setEditingLink] = useState<{
    link: Group["links"][0] | null;
    groupId: string;
  } | null>(null);
  const [showAddGroup, setShowAddGroup] = useState(false);
  const [showAddLink, setShowAddLink] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // Reset confirm delete after 3 seconds
  useEffect(() => {
    if (confirmDeleteId) {
      const timer = setTimeout(() => setConfirmDeleteId(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [confirmDeleteId]);

  // Filter groups/links by search query
  const filteredGroups = useMemo(() => {
    if (!searchQuery.trim()) return groups;

    const query = searchQuery.toLowerCase();
    return groups
      .map((group) => {
        // If group name matches, show all its links
        if (group.name.toLowerCase().includes(query)) {
          return group;
        }
        // Otherwise, filter links by name/tags
        return {
          ...group,
          links: group.links.filter(
            (link) =>
              link.name.toLowerCase().includes(query) ||
              link.tags.some((tag) => tag.toLowerCase().includes(query))
          ),
        };
      })
      .filter((group) => group.links.length > 0);
  }, [groups, searchQuery]);

  // Helper to highlight search matches
  const highlightMatch = (text: string) => {
    if (!searchQuery.trim()) return text;
    const query = searchQuery.trim();
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, i) =>
      regex.test(part) ? (
        <mark key={i} className="bg-yellow-200 text-gray-900 rounded px-0.5">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  // Check if a link has duplicates (returns warning messages or null)
  const getLinkDuplicateWarnings = (link: Group["links"][0], currentGroupId: string): string[] => {
    const warnings: string[] = [];
    const allLinks = groups.flatMap((g) =>
      g.links.map((l) => ({ ...l, groupId: g._id, groupName: g.name }))
    );

    // Check for duplicate name in same group
    const duplicateName = allLinks.find(
      (l) =>
        l._id !== link._id &&
        l.groupId === currentGroupId &&
        l.name.toLowerCase() === link.name.toLowerCase()
    );
    if (duplicateName) {
      warnings.push(`Duplicate name: another "${duplicateName.name}" exists in this group`);
    }

    // Check for duplicate URL (normalized)
    const normalizedUrl = normalizeUrl(link.url);
    const duplicateUrl = allLinks.find(
      (l) =>
        l._id !== link._id &&
        normalizeUrl(l.url) === normalizedUrl
    );
    if (duplicateUrl) {
      const isSameGroup = duplicateUrl.groupId === currentGroupId;
      if (link.url !== duplicateUrl.url) {
        // Different URL format but same normalized
        warnings.push(
          `Duplicate URL: "${duplicateUrl.name}"${isSameGroup ? '' : ` in ${duplicateUrl.groupName}`} has same URL (${duplicateUrl.url} ≈ ${link.url})`
        );
      } else {
        warnings.push(
          `Duplicate URL: same as "${duplicateUrl.name}"${isSameGroup ? '' : ` in ${duplicateUrl.groupName}`}`
        );
      }
    }

    return warnings;
  };

  // PostgreSQL mutations
  const createGroupMutation = useCreateGroup();
  const updateGroupMutation = useUpdateGroup();
  const deleteGroupMutation = useDeleteGroup();
  const createLinkMutation = useCreateLink();
  const updateLinkMutation = useUpdateLink();
  const deleteLinkMutation = useDeleteLink();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <HiCog6Tooth className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">Manage External Links</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded"
          >
            <HiXMark className="w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        <div className="px-6 py-3 border-b border-gray-200">
          <div className="relative">
            <input
              type="text"
              placeholder="Search links..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-maven-teal-500 focus:border-transparent"
            />
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="w-5 h-5 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
            >
              <path
                fillRule="evenodd"
                d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {filteredGroups.map((group) => {
              const allHidden = group.links.every((link) => hiddenLinkIds.has(link._id));

              return (
              <div key={group._id} className="border border-gray-200 rounded-lg p-4">
                {/* Group Header */}
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900">{highlightMatch(group.name)}</h4>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onToggleGroupVisibility(group)}
                      className={`p-1 rounded hover:bg-gray-100 ${allHidden ? "text-gray-400" : "text-gray-600"}`}
                      title={allHidden ? "Show all links" : "Hide all links"}
                    >
                      {allHidden ? (
                        <HiEyeSlash className="w-4 h-4" />
                      ) : (
                        <HiEye className="w-4 h-4" />
                      )}
                    </button>
                    <button
                      onClick={() => setEditingGroup(group)}
                      className="p-1 text-gray-400 hover:text-gray-600"
                      title="Edit group"
                    >
                      <HiPencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`Delete "${group.name}" and all its links?`)) {
                          deleteGroupMutation.mutate({ id: group._id, userEmail });
                        }
                      }}
                      className="p-1 text-gray-400 hover:text-red-600"
                      title="Delete group"
                    >
                      <HiTrash className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Links */}
                <div className="space-y-2">
                  {group.links.map((link) => {
                    const isHidden = hiddenLinkIds.has(link._id);
                    const duplicateWarnings = getLinkDuplicateWarnings(link, group._id);
                    return (
                    <div
                      key={link._id}
                      className={`flex items-center justify-between py-2 px-3 bg-gray-50 rounded ${isHidden ? "opacity-50" : ""}`}
                    >
                      <div className="flex items-center gap-2">
                        {duplicateWarnings.length > 0 && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-help">⚠️</span>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs">
                              <ul className="text-xs space-y-1">
                                {duplicateWarnings.map((warning, i) => (
                                  <li key={i}>{warning}</li>
                                ))}
                              </ul>
                            </TooltipContent>
                          </Tooltip>
                        )}
                        <div>
                          <span className="text-sm font-medium text-gray-900">
                            {highlightMatch(link.name)}
                          </span>
                          {link.tags.length > 0 && (
                            <span className="ml-2 text-xs text-gray-500">
                              ({link.tags.map((tag, i) => (
                                <span key={tag}>
                                  {i > 0 && ", "}
                                  {highlightMatch(tag)}
                                </span>
                              ))})
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => onToggleLinkVisibility(link._id)}
                          className={`p-1 rounded hover:bg-gray-200 ${isHidden ? "text-gray-400" : "text-gray-600"}`}
                          title={isHidden ? "Show link" : "Hide link"}
                        >
                          {isHidden ? (
                            <HiEyeSlash className="w-4 h-4" />
                          ) : (
                            <HiEye className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => setEditingLink({ link, groupId: group._id })}
                          className="p-1 text-gray-400 hover:text-gray-600"
                          title="Edit link"
                        >
                          <HiPencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            if (confirmDeleteId === link._id) {
                              deleteLinkMutation.mutate({ id: link._id, userEmail });
                              setConfirmDeleteId(null);
                            } else {
                              setConfirmDeleteId(link._id);
                            }
                          }}
                          className={`p-1 rounded transition-colors ${
                            confirmDeleteId === link._id
                              ? "text-red-600 bg-red-100"
                              : "text-gray-400 hover:text-red-600"
                          }`}
                          title={confirmDeleteId === link._id ? "Click again to confirm" : "Delete link"}
                        >
                          {confirmDeleteId === link._id ? (
                            <HiCheck className="w-4 h-4" />
                          ) : (
                            <HiTrash className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  );
                  })}
                </div>

                {/* Add Link Button */}
                <button
                  onClick={() => setShowAddLink(group._id)}
                  className="mt-3 flex items-center gap-1 text-sm text-maven-teal-500 hover:text-maven-teal-700"
                >
                  <HiPlus className="w-4 h-4" />
                  Add Link
                </button>
              </div>
              );
            })}

            {/* Add Group Button */}
            <button
              onClick={() => setShowAddGroup(true)}
              className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-maven-teal-500 hover:text-maven-teal-500 flex items-center justify-center gap-2"
            >
              <HiPlus className="w-5 h-5" />
              Add Group
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 text-sm font-medium text-white bg-maven-teal-500 rounded-full hover:bg-maven-teal-600"
          >
            Done
          </button>
        </div>
      </div>

      {/* Add Group Form */}
      {showAddGroup && (
        <GroupForm
          onSave={async (name) => {
            await createGroupMutation.mutateAsync({ name, createdBy: userEmail });
            setShowAddGroup(false);
          }}
          onCancel={() => setShowAddGroup(false)}
        />
      )}

      {/* Edit Group Form */}
      {editingGroup && (
        <GroupForm
          initialName={editingGroup.name}
          onSave={async (name) => {
            await updateGroupMutation.mutateAsync({ id: editingGroup._id, name, userEmail });
            setEditingGroup(null);
          }}
          onCancel={() => setEditingGroup(null)}
        />
      )}

      {/* Add Link Form */}
      {showAddLink && (
        <LinkForm
          groups={groups}
          initialGroupId={showAddLink}
          onSave={async (data) => {
            await createLinkMutation.mutateAsync({
              name: data.name,
              url: data.url,
              description: data.description,
              groupId: data.groupId,
              tags: data.tags,
              createdBy: userEmail,
            });
            setShowAddLink(null);
          }}
          onCancel={() => setShowAddLink(null)}
        />
      )}

      {/* Edit Link Form */}
      {editingLink && (
        <LinkForm
          groups={groups}
          initialGroupId={editingLink.groupId}
          initialLink={editingLink.link!}
          onSave={async (data) => {
            await updateLinkMutation.mutateAsync({
              id: editingLink.link!._id,
              name: data.name,
              url: data.url,
              description: data.description,
              groupId: data.groupId,
              tags: data.tags,
              userEmail,
            });
            setEditingLink(null);
          }}
          onCancel={() => setEditingLink(null)}
        />
      )}
    </div>
  );
}

// Group Form (Add/Edit)
function GroupForm({
  initialName = "",
  onSave,
  onCancel,
}: {
  initialName?: string;
  onSave: (name: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initialName);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    await onSave(name.trim());
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-lg w-full max-w-md p-6"
      >
        <h4 className="text-lg font-semibold mb-4">
          {initialName ? "Edit Group" : "Add Group"}
        </h4>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Group name"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-maven-teal-500"
          autoFocus
        />
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!name.trim() || saving}
            className="px-4 py-2 text-sm font-medium text-white bg-maven-teal-500 rounded-full hover:bg-maven-teal-600 disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

// Link Form (Add/Edit)
function LinkForm({
  groups,
  initialGroupId,
  initialLink,
  onSave,
  onCancel,
}: {
  groups: Group[];
  initialGroupId: string;
  initialLink?: Group["links"][0];
  onSave: (data: {
    name: string;
    url: string;
    description?: string;
    groupId: string;
    tags: string[];
  }) => Promise<void>;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initialLink?.name || "");
  const [url, setUrl] = useState(initialLink?.url || "");
  const [description, setDescription] = useState(initialLink?.description || "");
  const [groupId, setGroupId] = useState<string>(initialGroupId);
  const [tagsInput, setTagsInput] = useState(initialLink?.tags.join(", ") || "");
  const [saving, setSaving] = useState(false);

  // Check for duplicates
  const duplicateWarnings = useMemo(() => {
    const warnings: string[] = [];
    const currentLinkId = initialLink?._id;

    // Get all links across all groups
    const allLinks = groups.flatMap((g) =>
      g.links.map((link) => ({ ...link, groupId: g._id, groupName: g.name }))
    );

    // Check for duplicate name within the same group
    if (name.trim()) {
      const duplicateName = allLinks.find(
        (link) =>
          link._id !== currentLinkId &&
          link.groupId === groupId &&
          link.name.toLowerCase() === name.trim().toLowerCase()
      );
      if (duplicateName) {
        warnings.push(`Name "${name.trim()}" already exists in this group`);
      }
    }

    // Check for duplicate URL (normalized) across all groups
    if (url.trim()) {
      const normalizedInput = normalizeUrl(url.trim());
      const duplicateUrl = allLinks.find(
        (link) =>
          link._id !== currentLinkId &&
          normalizeUrl(link.url) === normalizedInput
      );
      if (duplicateUrl) {
        warnings.push(`URL already exists as "${duplicateUrl.name}" in ${duplicateUrl.groupName}`);
      }
    }

    return warnings;
  }, [name, url, groupId, groups, initialLink]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !url.trim()) return;
    setSaving(true);
    const tags = tagsInput
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    await onSave({ name: name.trim(), url: url.trim(), description: description.trim() || undefined, groupId, tags });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-lg w-full max-w-md p-6"
      >
        <h4 className="text-lg font-semibold mb-4">
          {initialLink ? "Edit Link" : "Add Link"}
        </h4>
        <div className="space-y-4">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Link name"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-maven-teal-500"
            autoFocus
          />
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="URL (https://...)"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-maven-teal-500"
          />
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Description (optional, shows on hover)"
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-maven-teal-500 resize-none"
          />
          <select
            value={groupId}
            onChange={(e) => setGroupId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-maven-teal-500"
          >
            {groups.map((g) => (
              <option key={g._id} value={g._id}>
                {g.name}
              </option>
            ))}
          </select>
          <input
            type="text"
            value={tagsInput}
            onChange={(e) => setTagsInput(e.target.value)}
            placeholder="Tags (comma-separated)"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-maven-teal-500"
          />
          {duplicateWarnings.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm font-medium text-yellow-800 mb-1">Possible duplicates:</p>
              <ul className="text-sm text-yellow-700 list-disc list-inside">
                {duplicateWarnings.map((warning, i) => (
                  <li key={i}>{warning}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!name.trim() || !url.trim() || saving}
            className="px-4 py-2 text-sm font-medium text-white bg-maven-teal-500 rounded-full hover:bg-maven-teal-600 disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

// Container for sortable groups (outer level)
function SortableGroupsContainer({
  groups,
  onGroupDragEnd,
  onLinkDragEnd,
}: {
  groups: Group[];
  onGroupDragEnd: (event: DragEndEvent) => void;
  onLinkDragEnd: (groupId: string, event: DragEndEvent) => void;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onGroupDragEnd}
    >
      <SortableContext
        items={groups.map((g) => g._id)}
        strategy={verticalListSortingStrategy}
      >
        <div className="space-y-10">
          {groups.map((group, groupIdx) => (
            <SortableGroup
              key={group._id}
              group={group}
              isLast={groupIdx === groups.length - 1}
              onLinkDragEnd={(event) => onLinkDragEnd(group._id, event)}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

// Sortable Group (for reordering categories)
function SortableGroup({
  group,
  isLast,
  onLinkDragEnd,
}: {
  group: Group;
  isLast: boolean;
  onLinkDragEnd: (event: DragEndEvent) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: group._id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={isDragging ? "opacity-50" : ""}
    >
      <div className="flex items-center gap-2 mb-3">
        <button
          {...attributes}
          {...listeners}
          className="text-white/50 hover:text-white cursor-grab active:cursor-grabbing p-1 -ml-1"
          title="Drag to reorder category"
        >
          <HiBars3 className="w-5 h-5" />
        </button>
        <h3 className="text-sm font-semibold text-white">
          {group.name}
        </h3>
      </div>
      <SortableLinkGroup group={group} onDragEnd={onLinkDragEnd} />
      {!isLast && <hr className="mt-6 border-white" />}
    </div>
  );
}

// Sortable Link Group (for reordering links within a category)
function SortableLinkGroup({
  group,
  onDragEnd,
}: {
  group: Group;
  onDragEnd: (event: DragEndEvent) => void;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 3,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
    >
      <SortableContext
        items={group.links.map((link) => link._id)}
        strategy={horizontalListSortingStrategy}
      >
        <div className="flex flex-wrap gap-2">
          {group.links.map((link, index) => (
            <SortableLink key={link._id} link={link} index={index} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

// Sortable Link Pill
function SortableLink({
  link,
  index,
}: {
  link: Group["links"][0];
  index: number;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: link._id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    touchAction: "none",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="select-none"
      {...attributes}
      {...listeners}
    >
      <div
        className={`px-4 py-2 text-sm border border-white rounded-full text-white cursor-grab active:cursor-grabbing inline-flex items-center ${
          isDragging ? "opacity-50" : index % 2 === 0 ? "wiggle-even" : "wiggle-odd"
        }`}
      >
        {link.name}
      </div>
    </div>
  );
}
